import { FieldArray, Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadProgram, canWriteProgram, getErrorObject,
  getProgramLegacyEnlistmentContents, isFormDataValid
} from '../../../utils/helper';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,
  Typography
} from '../../../utils/material-ui-core';

interface tmCRUDProps {
  open: boolean;
  programKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

// Default form value

const formDefaultValue = [
  {
    contentKey: "",
    value: "",
    multiLine: false,
    title: "",
    useDefault: true,
    defaultValue: "",
  },
];

// Form validation schema
const formValidationSchema = Yup.object().shape({});

const LegacyEnlistmentContentsCURD = ({ open, programKey, onCallback }: tmCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { refreshData, showAlert, userFunctionAccess } = useContext(AppContext);

  const [refreshParent, setRefreshParent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<LegacyEnlistmentContent[]>(formDefaultValue);

  const save = async () => {
    if (!canWriteProgram(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }

    const legacyEnlistmentContents: BaseLegacyEnlistmentContent[] = [];
    formReference.current.values.forEach((value) => {
      if (!value.useDefault) {
        //legacyEnlistmentContents.push({ contentKey: value.contentKey, value: btoa(value.value) });
        legacyEnlistmentContents.push({
          contentKey: value.contentKey,
          value: btoa(unescape(encodeURIComponent(value.value))),
        });
      }
    });

    const payload = {
      legacyEnlistmentContents: legacyEnlistmentContents,
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/programs/" + programKey + "/other/legacyenlistmentcontents", payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          setSaveButtonVisible(false);
          setSaveInProgress(false);
          setRefreshParent(true);
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "legacyenlistmentcontents.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save legacy enlistment contents."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something was wrong in our system."),
        severity: "error",
      });
    }
  };

  const cancelEdit = () => {
    formReference.current.resetForm({});
    if (refreshParent) {
      refreshData("programs");
      onCallback({ status: "refresh", data: "" });
      return;
    }
    onCallback({ status: "cancel", data: "" });
  };

  const cancel = () => {
    onCallback({ status: "cancel", data: "" });
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setLoading(true);
        setReadonly(true);
        setSaveButtonVisible(false);

        if (isFormDataValid(formReference)) {
          formReference.current.resetForm({});
        }

        if (!canReadProgram(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (programKey === "") {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        } else {
          const apiResponse = await getProgramLegacyEnlistmentContents(programKey);
          if (apiResponse.success) {
            const data = apiResponse.data;
            setInitialFormValues(data.legacyEnlistmentContents as LegacyEnlistmentContent[]);
            setReadonly(!canWriteProgram(userFunctionAccess));
            setLoading(false);
          } else {
            showAlert({ message: apiResponse.errorMessage, severity: "error" });
            cancel();
            return;
          }
        }
      }
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, programKey]);

  return (
    <>
      <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open} maxWidth={"md"}>
        <DialogTitle className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View Legacy Enlistment Content" : "Edit Legacy Enlistment Content"}
          </Typography>
        </DialogTitle>
        {loading ? (
          <React.Fragment>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <SaveBackdrop open={saveInProgress} />

              <Box className={pageStyles.contentText}>
                <Box p={1}></Box>

                <Formik
                  innerRef={formReference}
                  initialValues={initialFormValues}
                  validationSchema={formValidationSchema}
                  validateOnMount={true}
                  validateOnBlur={true}
                  validateOnChange={true}
                  onSubmit={(values: LegacyEnlistmentContent[]) => {
                    save();
                  }}
                >
                  {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, setFieldError, resetForm }) => (
                    <Form
                      onChange={() => {
                        setSaveButtonVisible(true);
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={2} sm={1}>
                          Default
                        </Grid>
                        <Grid item xs={10} sm={11}>
                          Title
                        </Grid>
                        <FieldArray name="legacyEnlistmentContent">
                          {({ insert, remove, push }) => (
                            <>
                              {values.length > 0 &&
                                values.map((item, index) => (
                                  <React.Fragment key={index}>
                                    <Grid item xs={2} sm={1}>
                                      <Checkbox
                                        disabled={readonly}
                                        id={`legacyEnlistmentContent.${index}.useDefault`}
                                        name={`legacyEnlistmentContent.${index}.useDefault`}
                                        checked={!item.useDefault}
                                        onChange={(event) => {
                                          handleChange(event);
                                          setFieldValue("[" + index + "].useDefault", !item.useDefault);
                                        }}
                                        inputProps={{
                                          "aria-label": "primary checkbox",
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={10} sm={11} key={index}>
                                      {item.useDefault ? (
                                        <TextField disabled={true} label={item.title} value={item.defaultValue} multiline={item.multiLine} />
                                      ) : (
                                        <TextField
                                          disabled={readonly}
                                          id={`legacyEnlistmentContent.${index}.value`}
                                          name={`legacyEnlistmentContent.${index}.value`}
                                          label={item.title}
                                          value={item.value}
                                          multiline={item.multiLine}
                                          onChange={(event) => {
                                            handleChange(event);
                                            setFieldValue("[" + index + "].value", event.target.value);
                                          }}
                                          onBlur={handleBlur}
                                        />
                                      )}
                                    </Grid>
                                  </React.Fragment>
                                ))}
                            </>
                          )}
                        </FieldArray>
                      </Grid>
                    </Form>
                  )}
                </Formik>
                <Box p={5}></Box>
              </Box>
            </DialogContent>
            <DialogActions>
              {!readonly ? (
                <Button disabled={!saveButtonVisible || saveInProgress} onClick={() => formReference.current.handleSubmit()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  Update
                </Button>
              ) : null}

              <Button onClick={() => cancelEdit()} color="secondary" autoFocus className={globalStyles.buttonDefault}>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </React.Fragment>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      minHeight: "400px",
    },
  })
);

export default LegacyEnlistmentContentsCURD;
